/**
 * TemplateEditor.js
 *
 * Full‑screen drag‑and‑drop WYSIWYG editor for DoqSolv templates.
 * Built with Tiptap (ProseMirror) for structured, extensible editing.
 *
 * Layout:
 *  ┌───────────────────────────────────┬──────────────┐
 *  │  Tiptap Editor (WYSIWYG)          │  Variable    │
 *  │                                   │  Sidebar     │
 *  │  (structured editing w/ chips)    │  (draggable  │
 *  │                                   │   chips)     │
 *  │                                   │              │
 *  │                                   │ + Add Var    │
 *  └───────────────────────────────────┴──────────────┘
 *
 * Variables are rendered as atomic `variableChip` nodes with a
 * `data-var-key` attribute. On save, we serialise the HTML
 * and extract the variables list automatically.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import VariableChip from './EditorExtensions/VariableChip';
import PDFOverlayEditor from './PDFOverlayEditor';
import { apiCall } from '../../utils/apiUtils';
import { toast } from 'react-toastify';

const API_BASE = '/api/external/v1/doqsolv';

// ─── Standard Variables (fetched from API, cached) ────────────────────────────

const DEFAULT_STANDARD = [
  { key: 'first_party_name', label: 'First Party Name', var_type: 'text', is_standard: true, is_required: true },
  { key: 'first_party_address', label: 'First Party Address', var_type: 'textarea', is_standard: true, is_required: false },
  { key: 'first_party_mobile', label: 'First Party Mobile', var_type: 'phone', is_standard: true, is_required: false },
  { key: 'second_party_name', label: 'Second Party Name', var_type: 'text', is_standard: true, is_required: true },
  { key: 'second_party_address', label: 'Second Party Address', var_type: 'textarea', is_standard: true, is_required: false },
  { key: 'second_party_mobile', label: 'Second Party Mobile', var_type: 'phone', is_standard: true, is_required: false },
  { key: 'stamp_denomination', label: 'Stamp Denomination', var_type: 'number', is_standard: true, is_required: false },
  { key: 'consideration_amount', label: 'Consideration Amount', var_type: 'number', is_standard: true, is_required: false },
  { key: 'stamp_state', label: 'Stamp State', var_type: 'text', is_standard: true, is_required: false },
  { key: 'execution_date', label: 'Execution Date', var_type: 'date', is_standard: true, is_required: false },
  { key: 'order_id', label: 'Order ID', var_type: 'text', is_standard: true, is_required: false },
];

// ─── Template Editor Component ────────────────────────────────────────────────

const TemplateEditor = ({ template, isAdmin, onClose, onSave }) => {
  // Form fields
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [category, setCategory] = useState(template?.category || '');
  const [scope, setScope] = useState(template?.scope || (isAdmin ? 'global' : 'client'));

  // Template type (html vs pdf_overlay)
  const [templateType, setTemplateType] = useState(template?.template_type || 'html');
  const [sourcePdfId, setSourcePdfId] = useState(template?.source_pdf_id || null);
  const [pdfFields, setPdfFields] = useState(template?.fields || []);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef(null);

  // Variables
  const [standardVars, setStandardVars] = useState(DEFAULT_STANDARD);
  const [customVars, setCustomVars] = useState([]);
  const [addVarOpen, setAddVarOpen] = useState(false);
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarLabel, setNewVarLabel] = useState('');
  const [newVarType, setNewVarType] = useState('text');

  // Variable settings modal
  const [settingsVar, setSettingsVar] = useState(null); // variable being configured

  // DOCX import
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // Assets
  const [sidebarTab, setSidebarTab] = useState('variables'); // 'variables' | 'assets'
  const [assets, setAssets] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const assetInputRef = useRef(null);

  // Saving
  const [saving, setSaving] = useState(false);

  // ── Tiptap Editor Setup ─────────────────────────────────────────────────

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'editor-image',
          style: 'max-width:100%;height:auto;border-radius:4px',
        },
      }),
      VariableChip,
    ],
    content: template?.html_content || '<p></p>',
    editorProps: {
      attributes: {
        class: 'prose max-w-none outline-none flex-1 p-6',
        style: "font-family:'Times New Roman',Times,serif;font-size:12pt;line-height:1.6;min-height:300px",
      },
      handleDrop: (view, event, slice, moved) => {
        // Handle variable chip drops from the sidebar
        try {
          const rawData = event.dataTransfer?.getData('text/plain');
          if (!rawData) return false;

          const varData = JSON.parse(rawData);
          if (!varData || !varData.key) return false;

          // Get the drop position
          const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
          if (!coordinates) return false;

          // Insert the variable chip at the drop position
          const { tr } = view.state;
          const node = view.state.schema.nodes.variableChip.create({
            key: varData.key,
            label: varData.label,
          });

          tr.insert(coordinates.pos, node);
          view.dispatch(tr);
          return true;
        } catch {
          return false;
        }
      },
    },
  });

  // ── Load standard variables from API ────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall(`${API_BASE}/variables`);
        if (res.variables && res.variables.length) {
          setStandardVars(res.variables);
        }
      } catch {
        // Fallback to defaults – already set
      }
    })();
  }, []);

  // ── Populate custom variables from existing template ────────────────────

  useEffect(() => {
    if (template?.variables) {
      const customs = template.variables.filter((v) => !v.is_standard);
      setCustomVars(customs);
    }
  }, [template]);

  // ── Load assets from API ────────────────────────────────────────────────

  const loadAssets = useCallback(async () => {
    try {
      const res = await apiCall(`${API_BASE}/assets`);
      if (res.assets) setAssets(res.assets);
    } catch {
      // Silently fail — assets are optional
    }
  }, []);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  // ── Asset Upload ────────────────────────────────────────────────────────

  const handleAssetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAsset(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/assets`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }

      toast.success('Image uploaded');
      await loadAssets();
    } catch (err) {
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setUploadingAsset(false);
      if (assetInputRef.current) assetInputRef.current.value = '';
    }
  };

  // ── Insert Image into Editor ────────────────────────────────────────────

  const insertAssetImage = useCallback((asset) => {
    if (!editor) return;
    const src = `${API_BASE}/assets/${asset.id}/content`;
    editor.chain().focus().setImage({ src, alt: asset.filename }).run();
  }, [editor]);

  // ── Delete Asset ────────────────────────────────────────────────────────

  const handleDeleteAsset = async (assetId) => {
    try {
      await apiCall(`${API_BASE}/assets/${assetId}`, { method: 'DELETE' });
      toast.success('Image deleted');
      await loadAssets();
    } catch (err) {
      toast.error(err.message || 'Failed to delete image');
    }
  };

  // ── Insert variable at cursor ───────────────────────────────────────────

  const insertVariable = useCallback((variable) => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'variableChip',
        attrs: { key: variable.key, label: variable.label },
      })
      .run();
  }, [editor]);

  // ── Drag & Drop ─────────────────────────────────────────────────────────

  const handleDragStart = useCallback((e, variable) => {
    e.dataTransfer.setData('text/plain', JSON.stringify(variable));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  // ── Add Custom Variable ─────────────────────────────────────────────────

  const handleAddVariable = () => {
    if (!newVarKey.trim() || !newVarLabel.trim()) {
      toast.warning('Variable key and label are required');
      return;
    }

    const sanitisedKey = newVarKey
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/^[^a-z]/, 'v_');

    // Check duplicates
    const allVars = [...standardVars, ...customVars];
    if (allVars.some((v) => v.key === sanitisedKey)) {
      toast.warning('A variable with this key already exists');
      return;
    }

    const newVar = {
      key: sanitisedKey,
      label: newVarLabel.trim(),
      var_type: newVarType,
      is_standard: false,
      is_required: true,
    };

    setCustomVars((prev) => [...prev, newVar]);
    setNewVarKey('');
    setNewVarLabel('');
    setNewVarType('text');
    setAddVarOpen(false);
    toast.success(`Variable "${newVar.label}" added`);
  };

  const removeCustomVar = (key) => {
    setCustomVars((prev) => prev.filter((v) => v.key !== key));
  };

  // Update a single field on the variable being configured
  const updateSettingsField = (field, value) => {
    setSettingsVar((prev) => ({ ...prev, [field]: value }));
    setCustomVars((prev) =>
      prev.map((v) => (v.key === settingsVar.key ? { ...v, [field]: value } : v))
    );
  };

  // ── DOCX Import ─────────────────────────────────────────────────────────

  const handleDocxImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/import-docx`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Import failed (${res.status})`);
      }

      const data = await res.json();

      // Set HTML content in Tiptap editor
      if (editor && data.html_content) {
        editor.commands.setContent(data.html_content);
      }

      // Auto-populate extracted variables
      if (data.variables && data.variables.length > 0) {
        const standardKeys = new Set(standardVars.map(v => v.key));
        const newCustoms = data.variables
          .filter(v => !standardKeys.has(v.key))
          .filter(v => !customVars.some(cv => cv.key === v.key))
          .map(v => ({
            key: v.key,
            label: v.label,
            var_type: 'text',
            is_standard: false,
            is_required: true,
          }));

        if (newCustoms.length > 0) {
          setCustomVars(prev => [...prev, ...newCustoms]);
        }
      }

      if (data.warnings && data.warnings.length > 0) {
        toast.info(`Import completed with ${data.warnings.length} warning(s)`);
      } else {
        toast.success(data.message || 'Document imported successfully');
      }
    } catch (err) {
      console.error('DOCX import error:', err);
      toast.error(err.message || 'Failed to import document.');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── DOCX Export ─────────────────────────────────────────────────────────

  const handleExportDocx = () => {
    if (!template?.id) {
      toast.warning('Save the template first before exporting');
      return;
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    window.open(`${backendUrl}${API_BASE}/templates/${template.id}/export`, '_blank');
  };

  // ── Extract variables from editor HTML ──────────────────────────────────

  const extractVariablesFromHTML = (html) => {
    const regex = /data-var-key="([^"]+)"/g;
    const usedKeys = new Set();
    let match;
    while ((match = regex.exec(html)) !== null) {
      usedKeys.add(match[1]);
    }
    return usedKeys;
  };

  // ── Source PDF Upload (for pdf_overlay templates) ───────────────────────

  const handleSourcePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/assets`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }

      const data = await res.json();
      setSourcePdfId(data.id);
      toast.success('Source PDF uploaded');
    } catch (err) {
      toast.error(err.message || 'Failed to upload source PDF');
    } finally {
      setUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  // ── Save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!name.trim()) {
      toast.warning('Template name is required');
      return;
    }

    // Validate based on template type
    if (templateType === 'html') {
      if (!editor || editor.isEmpty) {
        toast.warning('Template content cannot be empty');
        return;
      }
    } else if (templateType === 'pdf_overlay') {
      if (!sourcePdfId) {
        toast.warning('Please upload a source PDF first');
        return;
      }
    }

    setSaving(true);
    try {
      let payload;

      if (templateType === 'pdf_overlay') {
        // PDF Overlay payload
        const usedKeys = new Set(pdfFields.map((f) => f.key));
        const allVars = [...standardVars, ...customVars];
        const finalVars = allVars
          .filter((v) => usedKeys.has(v.key))
          .map((v) => ({
            key: v.key,
            label: v.label,
            var_type: v.var_type || 'text',
            is_standard: !!v.is_standard,
            is_required: v.is_required !== false,
            default_value: v.default_value || null,
            uppercase: v.uppercase || false,
            max_length: v.max_length || null,
            group: v.group || null,
          }));

        payload = {
          name: name.trim(),
          description: description.trim() || null,
          category: category.trim() || null,
          template_type: 'pdf_overlay',
          source_pdf_id: sourcePdfId,
          fields: pdfFields,
          variables: finalVars,
        };
      } else {
        // HTML payload
        const htmlContent = editor.getHTML();
        const usedKeys = extractVariablesFromHTML(htmlContent);
        const allVars = [...standardVars, ...customVars];
        const finalVars = allVars
          .filter((v) => usedKeys.has(v.key))
          .map((v) => ({
            key: v.key,
            label: v.label,
            var_type: v.var_type || 'text',
            is_standard: !!v.is_standard,
            is_required: v.is_required !== false,
            default_value: v.default_value || null,
            uppercase: v.uppercase || false,
            max_length: v.max_length || null,
            group: v.group || null,
          }));

        payload = {
          name: name.trim(),
          description: description.trim() || null,
          category: category.trim() || null,
          template_type: 'html',
          html_content: htmlContent,
          variables: finalVars,
        };
      }

      if (template?.id) {
        await apiCall(`${API_BASE}/templates/${template.id}`, {
          method: 'PUT',
          data: payload,
        });
        toast.success('Template updated');
      } else {
        const scopeParam = isAdmin ? `?scope=${scope}` : '';
        await apiCall(`${API_BASE}/templates${scopeParam}`, {
          method: 'POST',
          data: payload,
        });
        toast.success('Template created');
      }

      onSave();
    } catch (err) {
      toast.error(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // ── Toolbar Button Helper ──────────────────────────────────────────────

  const ToolbarBtn = ({ onClick, active, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      className={`p-1.5 rounded transition ${
        active ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-200 text-gray-700'
      }`}
      title={title}
    >
      {children}
    </button>
  );

  // ── Render ──────────────────────────────────────────────────────────────

  if (!editor) return null;

  return (
    <div className="fade-in flex flex-col h-screen overflow-hidden">
      {/* Top Bar */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm px-6 py-3 mb-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition p-1"
            title="Back to Templates"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h2 className="text-lg font-semibold text-gray-900">
            {template ? `Edit: ${template.name}` : 'New Template'}
          </h2>
        </div>

        <div className="flex items-center gap-3">
          {/* Contextual upload button: DOCX for HTML, PDF for overlay */}
          {templateType === 'pdf_overlay' ? (
            <>
              <input
                ref={pdfInputRef}
                type="file"
                accept=".pdf"
                onChange={handleSourcePdfUpload}
                className="hidden"
              />
              <button
                onClick={() => pdfInputRef.current?.click()}
                disabled={uploadingPdf}
                className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {uploadingPdf ? 'Uploading…' : sourcePdfId ? 'Replace PDF' : 'Upload PDF'}
              </button>
              {sourcePdfId && (
                <span className="text-xs text-green-600 font-medium">✓ PDF ready</span>
              )}
            </>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                onChange={handleDocxImport}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {importing ? 'Importing…' : 'Import .docx'}
              </button>

              {template?.id && (
                <button
                  onClick={handleExportDocx}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                  title="Export template as Word document"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export .docx
                </button>
              )}
            </>
          )}

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition"
            style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Saving…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Template
              </>
            )}
          </button>
        </div>
      </div>

      {/* Meta fields row */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm px-6 py-3 mb-3 flex-shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Template Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Partnership Deed"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Category</label>
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Deed, Rent Agreement"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description…"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          {isAdmin && (
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Scope</label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              >
                <option value="global">Global Default</option>
                <option value="client">Client‑specific</option>
              </select>
            </div>
          )}
          {/* Template Type Selector */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Template Type</label>
            <select
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value)}
              disabled={!!template?.id}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            >
              <option value="html">HTML Editor</option>
              <option value="pdf_overlay">PDF Overlay</option>
            </select>
          </div>
        </div>
      </div>

      {/* Editor + Sidebar */}
      <div className="flex gap-3 flex-1 min-h-0 overflow-hidden">
        {/* Conditional Editor: HTML (Tiptap) vs PDF Overlay */}
        {templateType === 'pdf_overlay' ? (
          <PDFOverlayEditor
            sourcePdfId={sourcePdfId}
            fields={pdfFields}
            onFieldsChange={setPdfFields}
            variables={customVars}
            standardVars={standardVars}
            onAddVariable={(newVar) => setCustomVars((prev) => [...prev, newVar])}
            onRemoveVariable={(key) => setCustomVars((prev) => prev.filter((v) => v.key !== key))}
            onUpdateVariable={(key, updates) => setCustomVars((prev) =>
              prev.map((v) => (v.key === key ? { ...v, ...updates } : v))
            )}
          />
        ) : (
          <>
            {/* HTML Editor Area */}
            <div className="flex-1 flex flex-col bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
              {/* Toolbar */}
              <div className="border-b border-gray-200 px-4 py-2 flex items-center gap-1 flex-shrink-0 bg-gray-50 flex-wrap">
                {/* Bold / Italic / Underline */}
                <ToolbarBtn
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  active={editor.isActive('bold')}
                  title="Bold"
                >
                  <strong className="text-sm">B</strong>
                </ToolbarBtn>
                <ToolbarBtn
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  active={editor.isActive('italic')}
                  title="Italic"
                >
                  <em className="text-sm">I</em>
                </ToolbarBtn>
                <ToolbarBtn
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  active={editor.isActive('underline')}
                  title="Underline"
                >
                  <span className="text-sm underline">U</span>
                </ToolbarBtn>

                <span className="w-px h-5 bg-gray-300 mx-1" />

                {/* Alignment */}
                <ToolbarBtn
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  active={editor.isActive({ textAlign: 'left' })}
                  title="Align Left"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M3 3h18v2H3V3zm0 8h12v2H3v-2zm0 8h18v2H3v-2zm0-4h12v2H3v-2z" /></svg>
                </ToolbarBtn>
                <ToolbarBtn
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  active={editor.isActive({ textAlign: 'center' })}
                  title="Center"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M3 3h18v2H3V3zm4 8h10v2H7v-2zm-4 8h18v2H3v-2zm4-4h10v2H7v-2z" /></svg>
                </ToolbarBtn>
                <ToolbarBtn
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  active={editor.isActive({ textAlign: 'right' })}
                  title="Align Right"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M3 3h18v2H3V3zm6 8h12v2H9v-2zm-6 8h18v2H3v-2zm6-4h12v2H9v-2z" /></svg>
                </ToolbarBtn>

                <span className="w-px h-5 bg-gray-300 mx-1" />

                {/* Lists */}
                <ToolbarBtn
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  active={editor.isActive('bulletList')}
                  title="Bullet List"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z" /></svg>
                </ToolbarBtn>
                <ToolbarBtn
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  active={editor.isActive('orderedList')}
                  title="Numbered List"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z" /></svg>
                </ToolbarBtn>

                <span className="w-px h-5 bg-gray-300 mx-1" />

                {/* Headings */}
                <select
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'p') {
                      editor.chain().focus().setParagraph().run();
                    } else {
                      const level = parseInt(val.replace('h', ''), 10);
                      editor.chain().focus().toggleHeading({ level }).run();
                    }
                    e.target.value = '';
                  }}
                  defaultValue=""
                  className="text-xs px-2 py-1 border border-gray-200 rounded bg-white"
                >
                  <option value="" disabled>Heading…</option>
                  <option value="p">Paragraph</option>
                  <option value="h1">Heading 1</option>
                  <option value="h2">Heading 2</option>
                  <option value="h3">Heading 3</option>
                </select>
              </div>

              {/* Tiptap Editor Content */}
              <div className="flex-1 overflow-y-auto min-h-0">
                <EditorContent
                  editor={editor}
                  className="h-full"
                  style={{ height: '100%' }}
                />
              </div>
            </div>

        {/* Sidebar (Variables | Assets) */}
        <div className="w-72 flex-shrink-0 bg-white rounded-lg border border-gray-100 shadow-sm flex flex-col overflow-hidden">
          {/* Tab Switcher */}
          <div className="flex border-b border-gray-200 flex-shrink-0">
            <button
              onClick={() => setSidebarTab('variables')}
              className={`flex-1 py-2.5 text-xs font-semibold transition ${
                sidebarTab === 'variables'
                  ? 'text-indigo-700 border-b-2 border-indigo-500 bg-white'
                  : 'text-gray-500 hover:text-gray-700 bg-gray-50'
              }`}
            >
              Variables
            </button>
            <button
              onClick={() => setSidebarTab('assets')}
              className={`flex-1 py-2.5 text-xs font-semibold transition ${
                sidebarTab === 'assets'
                  ? 'text-indigo-700 border-b-2 border-indigo-500 bg-white'
                  : 'text-gray-500 hover:text-gray-700 bg-gray-50'
              }`}
            >
              Assets
            </button>
          </div>

          {/* ─── Variables Tab ─── */}
          {sidebarTab === 'variables' && (
            <>
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
                {/* Standard Variables */}
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                    Standard (from Order)
                  </p>
                  <div className="space-y-1">
                    {standardVars.map((v) => (
                      <div
                        key={v.key}
                        draggable
                        onDragStart={(e) => handleDragStart(e, v)}
                        onClick={() => insertVariable(v)}
                        className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium cursor-grab active:cursor-grabbing hover:bg-indigo-100 transition select-none border border-transparent hover:border-indigo-200"
                        title={`Drag to insert {{${v.key}}}`}
                      >
                        <svg className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                        <span className="truncate">{v.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Custom Variables */}
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                    Custom Variables
                  </p>
                  {customVars.length === 0 && !addVarOpen && (
                    <p className="text-xs text-gray-400 italic mb-2">No custom variables yet.</p>
                  )}
                  <div className="space-y-1">
                    {customVars.map((v) => (
                      <div
                        key={v.key}
                        draggable
                        onDragStart={(e) => handleDragStart(e, v)}
                        onClick={() => insertVariable(v)}
                        className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium cursor-grab active:cursor-grabbing hover:bg-emerald-100 transition select-none border border-transparent hover:border-emerald-200"
                        title={`Drag to insert {{${v.key}}}`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <svg className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                          </svg>
                          <span className="truncate">{v.label}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSettingsVar(v); }}
                            className="text-emerald-400 hover:text-emerald-600 flex-shrink-0"
                            title="Variable settings"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); removeCustomVar(v.key); }}
                            className="text-red-400 hover:text-red-600 flex-shrink-0"
                            title="Remove variable"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Inline Variable Settings Panel */}
                  {settingsVar && (
                    <div className="mt-2 p-3 bg-indigo-50 rounded-lg border border-indigo-200 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-indigo-700">⚙ {settingsVar.label}</p>
                        <button
                          onClick={() => setSettingsVar(null)}
                          className="text-gray-400 hover:text-gray-600 text-xs"
                        >
                          ✕
                        </button>
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 block">Label</label>
                        <input
                          value={settingsVar.label}
                          onChange={(e) => updateSettingsField('label', e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 block">Type</label>
                        <select
                          value={settingsVar.var_type || 'text'}
                          onChange={(e) => updateSettingsField('var_type', e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                        >
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="textarea">Textarea</option>
                          <option value="currency">Currency (₹)</option>
                          <option value="mobile">Mobile (Indian)</option>
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="select">Dropdown</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 block">Default Value</label>
                        <input
                          value={settingsVar.default_value || ''}
                          onChange={(e) => updateSettingsField('default_value', e.target.value || null)}
                          placeholder="None"
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 block">Group</label>
                        <input
                          value={settingsVar.group || ''}
                          onChange={(e) => updateSettingsField('group', e.target.value || null)}
                          placeholder='e.g. "First Party"'
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 block">Max Length</label>
                        <input
                          type="number"
                          value={settingsVar.max_length || ''}
                          onChange={(e) => updateSettingsField('max_length', e.target.value ? parseInt(e.target.value, 10) : null)}
                          placeholder="No limit"
                          min="1"
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                        />
                      </div>
                      <div className="flex gap-3">
                        <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!settingsVar.uppercase}
                            onChange={(e) => updateSettingsField('uppercase', e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600"
                          />
                          Uppercase
                        </label>
                        <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settingsVar.is_required !== false}
                            onChange={(e) => updateSettingsField('is_required', e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600"
                          />
                          Required
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Add Variable Form */}
                  {addVarOpen ? (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                      <input
                        value={newVarLabel}
                        onChange={(e) => {
                          setNewVarLabel(e.target.value);
                          setNewVarKey(
                            e.target.value
                              .trim()
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, '_')
                              .replace(/^[^a-z]/, 'v_')
                          );
                        }}
                        placeholder="Label (e.g. Monthly Rent)"
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                      />
                      <div className="flex gap-2">
                        <input
                          value={newVarKey}
                          onChange={(e) => setNewVarKey(e.target.value)}
                          placeholder="Key"
                          className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none font-mono"
                        />
                        <select
                          value={newVarType}
                          onChange={(e) => setNewVarType(e.target.value)}
                          className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500 outline-none"
                        >
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="textarea">Textarea</option>
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleAddVariable}
                          className="flex-1 text-xs text-white px-3 py-1.5 rounded font-medium"
                          style={{ background: '#10b981' }}
                        >
                          Add
                        </button>
                        <button
                          onClick={() => { setAddVarOpen(false); setNewVarKey(''); setNewVarLabel(''); }}
                          className="flex-1 text-xs text-gray-600 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddVarOpen(true)}
                      className="mt-2 w-full flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-indigo-600 border border-dashed border-indigo-300 rounded-lg hover:bg-indigo-50 transition"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Variable
                    </button>
                  )}
                </div>
              </div>

              <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex-shrink-0">
                <p className="text-[10px] text-gray-400 leading-tight">
                  💡 Drag a variable chip onto the editor or click to insert at cursor.
                </p>
              </div>
            </>
          )}

          {/* ─── Assets Tab ─── */}
          {sidebarTab === 'assets' && (
            <>
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                {/* Upload Button */}
                <div>
                  <input
                    ref={assetInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,application/pdf"
                    onChange={handleAssetUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => assetInputRef.current?.click()}
                    disabled={uploadingAsset}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium text-white rounded-lg transition"
                    style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
                  >
                    {uploadingAsset ? (
                      <>
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                        Uploading…
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Upload Image
                      </>
                    )}
                  </button>
                </div>

                {/* Asset Grid */}
                {assets.length === 0 ? (
                  <p className="text-xs text-gray-400 italic text-center py-4">No images uploaded yet.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {assets.map((asset) => (
                      <div
                        key={asset.id}
                        className="group relative bg-gray-50 rounded-lg border border-gray-200 overflow-hidden hover:border-indigo-300 transition cursor-pointer"
                        onClick={() => insertAssetImage(asset)}
                        title={`Click to insert: ${asset.filename}`}
                      >
                        <img
                          src={`${API_BASE}/assets/${asset.id}/content`}
                          alt={asset.filename}
                          className="w-full h-20 object-cover"
                          loading="lazy"
                        />
                        <div className="px-1.5 py-1">
                          <p className="text-[10px] text-gray-500 truncate">{asset.filename}</p>
                        </div>
                        {/* Delete overlay */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAsset(asset.id); }}
                          className="absolute top-1 right-1 p-0.5 rounded bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete image"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex-shrink-0">
                <p className="text-[10px] text-gray-400 leading-tight">
                  🖼️ Click an image to insert it at the cursor position in the editor.
                </p>
              </div>
            </>
          )}
        </div>
          </>
        )}
      </div>

    </div>
  );
};

export default TemplateEditor;
